import { makeAutoObservable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import {
  SOLUTION_TYPE,
  SolutionType,
  SolutionTypeFlags,
} from '../constants/user-info/solutionType';
import { LOCAL_STORAGE_KEYS } from '../constants/system/LOCAL_STORAGE_KEYS';
import { resetAllStore } from './resetAllStore';
import { broadcastEvent, goToHomePage } from '../utils/angularUtils';
import { getFlagsObject } from '../utils/sortingAndGroupingUtil/getFlagsObject/getFlagsObject';
import { ANGULAR_EVENT_KEYS } from '../constants/system/ANGULAR_EVENT_KEYS';

type SolutionsInfo = {
  solutions: SolutionType[];
  solutionTypeFlags: SolutionTypeFlags;
};

// The solutions that should be used as default if they are available, order is important first priority solution is used if available
const solutionsPriorityOrder: SolutionType[] = [
  SOLUTION_TYPE.NUTRITION,
  SOLUTION_TYPE.SCAN_IQ,
  SOLUTION_TYPE.CHALLENGE,
];

class SolutionsStore {
  activeSolution: SolutionType | null = null;
  activeSolutionFlags: SolutionTypeFlags = {};
  availableSolutions: SolutionType[] = [];
  solutionTypeFlags: SolutionTypeFlags = {};

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    makePersistable(this, {
      name: 'SolutionsStore',
      properties: [
        'activeSolution',
        'activeSolutionFlags',
        'solutionTypeFlags',
        'availableSolutions',
      ],
      storage: window.localStorage,
    });
  }

  setDefaultValues() {
    this.activeSolution = null;
    this.activeSolutionFlags = {};
    this.availableSolutions = [];
    this.solutionTypeFlags = {};
  }

  setSolutionsInfo({ solutions, solutionTypeFlags }: SolutionsInfo) {
    this.setAvailableSolutions(solutions);
    this.setSolutionTypeFlags(solutionTypeFlags);

    // set default solution if it not exist or is not available now
    if (this.activeSolution === null || !solutions.includes(this.activeSolution)) {
      this.setDefaultSolution();
    }
  }

  private setDefaultSolution() {
    if (this.availableSolutions.length === 1) {
      this.setActiveSolutionData(this.availableSolutions[0]);
      return;
    }

    const defaultSolution = solutionsPriorityOrder.find((solution) =>
      this.availableSolutions.includes(solution),
    );

    this.setActiveSolutionData(defaultSolution ?? this.availableSolutions[0]);
  }

  private setActiveSolutionData(solution: SolutionType) {
    this.setActiveSolution(solution);
    this.setActiveSolutionFlags(solution);
    // trigger storage event to reload the other tabs with the new solution
    localStorage.setItem(LOCAL_STORAGE_KEYS.ACTIVE_SOLUTION, solution);
    // Broadcast Angular event for same-tab reactivity
    broadcastEvent(ANGULAR_EVENT_KEYS.ACTIVE_SOLUTION_CHANGED);
  }

  handleSolutionChange(solution: SolutionType) {
    this.setActiveSolutionData(solution);
    goToHomePage();
  }

  private setActiveSolution(solution: SolutionType) {
    this.activeSolution = solution;
  }

  private setActiveSolutionFlags(activeSolution: SolutionType) {
    this.activeSolutionFlags = getFlagsObject(SOLUTION_TYPE, [activeSolution]);
  }

  private setSolutionTypeFlags(solutionTypeFlags: SolutionTypeFlags) {
    this.solutionTypeFlags = solutionTypeFlags;
  }

  private setAvailableSolutions(availableSolutions: SolutionType[]) {
    this.availableSolutions = availableSolutions;
  }
}

const solutionsStore = new SolutionsStore();

resetAllStore.addResetMethod(solutionsStore.setDefaultValues);

export { solutionsStore, SolutionsStore };
